import { useLazyLoadQuery } from 'react-relay';
import { Badge, BlockStack, Box, Card, InlineStack, PageHeader, Typography } from '@pv-frontend/ui-components-web';
import type { BadgeTone } from '@pv-frontend/ui-components-web';
import { readEnv } from '../env';
import { systemStatusQuery } from '../relay/system-status-query';
import type { systemStatusQuery as SystemStatusQuery } from '../relay/__generated__/systemStatusQuery.graphql';
import { toServiceViews, type LiveServiceStatus } from '../ops/services';
import { OpsBoard } from './ops-board';

/**
 * The ops "all-green" status board (pv-frontend#32). Runs the live `systemStatus`
 * query (pv-mono#70) and maps it into the presentational `OpsBoard`: per-service
 * health + deploy-integrity + running build, a single top-level verdict, and the
 * WebFetch definitions currently drifting. The board is data-driven — its rows
 * are exactly the services the backend monitors.
 */
export function OpsScreen() {
    const { entraTenantId } = readEnv();
    const data = useLazyLoadQuery<SystemStatusQuery>(
        systemStatusQuery,
        {},
        { fetchPolicy: 'store-or-network' },
    );

    const live: LiveServiceStatus[] = data.systemStatus.services.map((service) => ({
        name: service.name,
        deploy: service.deploy,
        health: { live: service.health.live, ready: service.health.ready },
        runningVersion: service.runningVersion ?? null,
        runningCommit: service.runningCommit ?? null,
        shippedVersion: service.shippedVersion ?? null,
    }));
    const services = toServiceViews(live, entraTenantId);
    const drift = data.systemStatus.webFetchDrift;

    return (
        <Box padding="600" maxWidth={960}>
            <BlockStack gap="500">
                <PageHeader
                    title="Service status"
                    description="Per-service health, deploy integrity and running build at a glance — with deep-links into the Azure blade for detail."
                />
                <OpsBoard services={services} />
                {drift.length > 0 ? <WebFetchDrift items={drift} /> : null}
            </BlockStack>
        </Box>
    );
}

const DRIFT_STATUS: Record<string, { label: string; tone: BadgeTone }> = {
    DRIFTING: { label: 'Parsing drift', tone: 'critical' },
    ACCESS_IMPAIRED: { label: 'Access blocked', tone: 'warning' },
};

/**
 * Fleet-wide WebFetch parse-drift — not per-service, so it sits below the board.
 * The backend already filters to the actionable definitions (drifting / access
 * impaired), most-actionable first.
 */
function WebFetchDrift({
    items,
}: {
    items: SystemStatusQuery['response']['systemStatus']['webFetchDrift'];
}) {
    return (
        <BlockStack gap="300">
            <Typography variant="heading-sm" as="h2" fontWeight="semibold">
                WebFetch drift
            </Typography>
            <Card padding="0">
                {items.map((item) => {
                    const status = DRIFT_STATUS[item.status] ?? {
                        label: item.status,
                        tone: 'neutral' as BadgeTone,
                    };
                    return (
                        <Box
                            key={item.definitionId}
                            paddingInline="400"
                            paddingBlock="400"
                            borderColor="border-card"
                            borderBlockStartWidth="0165"
                        >
                            <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
                                <BlockStack gap="050">
                                    <Typography variant="body-md" fontWeight="semibold">
                                        {item.name}
                                    </Typography>
                                    <Typography variant="body-sm" tone="text-secondary">
                                        {`${item.ok}/${item.total} parsed OK · ${item.parseFailed} failed · ${item.parseEmpty} empty · ${item.accessBlocked} blocked`}
                                    </Typography>
                                </BlockStack>
                                <Badge tone={status.tone} dot>
                                    {status.label}
                                </Badge>
                            </InlineStack>
                        </Box>
                    );
                })}
            </Card>
        </BlockStack>
    );
}
